import React, { Component } from 'react';
import { Box, Button, Chip } from '@mui/material';

import { InteriorConditionSettings } from '@paragon/contract-library';
import { distanceGreaterThan } from '@paragon/geometry-library';

import BeamPocket from '../../../../../../resources/BeamPocket.png';
import InteriorBearing from '../../../../../../resources/InteriorBearing.png';
import Step from '../../../../../../resources/StepUp.png';
import Tray from '../../../../../../resources/Tray.png';
import { updateActiveInteriorConditionSettings } from '../../../../../store/actions/editorActions';
import { getActiveFeaturePatternTrussSettings } from '../../../../../store/selectors/activeComponentFeaturePatternSettingsSelectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { BeamPocketSettingsProperties } from './BeamPocketSettingsProperties';
import {
  createBeamPocketSettings,
  createInteriorConditionNameGenerator,
  createStepSettings,
  createTraySettings,
  createVerticalStackSettings,
} from './interiorConditionUtilities';
import { StepSettingsProperties } from './StepSettingsProperties';
import { TraySettingsProperties } from './TraySettings';
import { VerticalStackSettingsProperties } from './VerticalStackSettingsProperties';

interface InteriorConditionButtonProps {
  readonly src: string;
  readonly alt: string;
  readonly onClick: () => void;
  readonly isSelected: boolean;
}

function InteriorConditionButton({ src, alt, onClick, isSelected }: InteriorConditionButtonProps) {
  return (
    <Box
      component="img"
      sx={[
        { cursor: 'pointer', height: 75, m: 1 },
        isSelected ? { border: '3px solid', borderColor: 'primary.main', borderRadius: '10%' } : null,
      ]}
      onClick={onClick}
      src={src}
      alt={alt}
    />
  );
}

type InteriorConditionStateSetting = 'blank' | 'step' | 'tray' | 'verticalStack' | 'beamPocket';

interface State {
  // This property being string | null was causing problems with PropertySelectField, so an empty string is used to
  //   signify no selection.
  readonly activeSettingsGuid: string;
  readonly selectedCondition: InteriorConditionStateSetting;
}

interface Props {
  readonly values: readonly InteriorConditionSettings[];
  readonly onChange: (newValues: InteriorConditionSettings[]) => void;
}

export class InteriorConditionSettingsProperties extends Component<Props, State> {
  state: State = {
    activeSettingsGuid: '',
    selectedCondition: 'blank',
  };

  onCreateNewStepSettings = () => {
    const newSettings = createStepSettings();

    this.onSelectSettings(newSettings.guid, newSettings.type);
    this.setSettings(newSettings);
  };

  onCreateNewTraySettings = () => {
    const newSettings = createTraySettings();

    this.onSelectSettings(newSettings.guid, newSettings.type);
    this.setSettings(newSettings);
  };

  onCreateNewVerticalStackSettings = () => {
    const newSettings = createVerticalStackSettings();

    this.onSelectSettings(newSettings.guid, newSettings.type);
    this.setSettings(newSettings);
  };

  onCreateNewBeamPocketSettings = () => {
    const newSettings = createBeamPocketSettings();

    this.onSelectSettings(newSettings.guid, newSettings.type);
    this.setSettings(newSettings);
  };

  onSelectSettings = (guid: string, condition?: InteriorConditionStateSetting) => {
    this.setState({ activeSettingsGuid: guid });
    !condition ? this.setState({ selectedCondition: 'blank' }) : this.setState({ selectedCondition: condition });
  };

  onSelectChip = (guid: string) => {
    this.setState({ selectedCondition: 'blank' });
    this.setState({ activeSettingsGuid: guid });
  };

  onDeleteActiveSettings = () => {
    this.setState({ selectedCondition: 'blank' });
    const deletedGuid = this.state.activeSettingsGuid;
    if (deletedGuid == null) {
      return;
    }

    const filteredSettings = this.props.values.filter((settings) => settings.guid !== deletedGuid);
    this.props.onChange(filteredSettings);

    if (this.state.activeSettingsGuid === deletedGuid) {
      this.setState((state, props) => ({
        activeSettingsGuid: props.values.find((settings) => settings.guid !== deletedGuid)?.guid ?? '',
      }));
    }
  };

  setSettings = (newSettings: InteriorConditionSettings) => {
    this.setState({ selectedCondition: newSettings.type });
    let updatedExistingSettings = false;

    let updatedSettingsArray = this.props.values.map((settings) => {
      if (settings.guid === newSettings.guid) {
        updatedExistingSettings = true;
        this.setState({ selectedCondition: 'blank' });
        return newSettings;
      }
      return settings;
    });

    if (!updatedExistingSettings) {
      updatedSettingsArray = [
        ...updatedSettingsArray.filter((condition) => distanceGreaterThan(condition.distanceFromLeft, 0)),
        newSettings,
      ];
    }

    this.props.onChange(updatedSettingsArray);
  };

  renderSettingsPanel(settings: InteriorConditionSettings) {
    switch (settings.type) {
      case 'beamPocket':
        return <BeamPocketSettingsProperties value={settings} onChange={this.setSettings} />;

      case 'verticalStack':
        return <VerticalStackSettingsProperties value={settings} onChange={this.setSettings} />;

      case 'step':
        return <StepSettingsProperties value={settings} onChange={this.setSettings} />;

      case 'tray':
        return <TraySettingsProperties value={settings} onChange={this.setSettings} />;

      default:
        console.error('Unrecognized settings object.');
        return null;
    }
  }

  render() {
    const allSettings = this.props.values;
    const activeSettings =
      this.state.activeSettingsGuid && allSettings.find((settings) => settings.guid === this.state.activeSettingsGuid);

    const generateSettingsName = createInteriorConditionNameGenerator();
    const generateSettingsNameFromGuid = (guid: string) => {
      if (guid === '') {
        return 'None';
      }

      const targetSettings = allSettings.find((settings) => settings.guid === guid);
      if (targetSettings && targetSettings.type === 'step') {
        return generateSettingsName(targetSettings, targetSettings.stepDirection);
      }
      if (targetSettings) return generateSettingsName(targetSettings);

      console.error('Attempted to generate name for non-existent settings object');
      return '<unknown settings>';
    };

    // Used to determine available options in the dropdown, so if nothing is selected that must be an option as well.
    const settingsGuidOptions = allSettings.map((settings) => settings.guid);
    if (this.state.activeSettingsGuid === '') {
      settingsGuidOptions.push('');
    }

    const chips = this.props.values.map((value) => (
      <Box component="li" sx={{ m: 1 }} key={value.guid}>
        <Chip
          color={this.state.activeSettingsGuid === value.guid ? 'primary' : 'default'}
          onClick={() => {
            this.onSelectChip(value.guid);
          }}
          label={generateSettingsNameFromGuid(value.guid)}
        />
      </Box>
    ));

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
          <InteriorConditionButton
            src={Step}
            alt="Step"
            onClick={this.onCreateNewStepSettings}
            isSelected={this.state.selectedCondition === 'step'}
          />
          <InteriorConditionButton
            src={Tray}
            alt="Tray"
            onClick={this.onCreateNewTraySettings}
            isSelected={this.state.selectedCondition === 'tray'}
          />
          <InteriorConditionButton
            src={InteriorBearing}
            alt="Interior Bearing"
            onClick={this.onCreateNewVerticalStackSettings}
            isSelected={this.state.selectedCondition === 'verticalStack'}
          />
          <InteriorConditionButton
            src={BeamPocket}
            alt="Beam Pocket"
            onClick={this.onCreateNewBeamPocketSettings}
            isSelected={this.state.selectedCondition === 'beamPocket'}
          />
        </Box>

        {this.state.activeSettingsGuid ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              listStyle: 'none',
              p: 0.5,
              m: 1,
            }}
            component="ul"
          >
            {chips}
          </Box>
        ) : null}

        {activeSettings ? this.renderSettingsPanel(activeSettings) : null}
        {activeSettings ? (
          <Box sx={{ display: 'flex', m: 1 }}>
            <Button
              sx={{ m: 'auto', width: 175 }}
              variant="outlined"
              disabled={this.state.activeSettingsGuid === ''}
              onClick={this.onDeleteActiveSettings}
            >
              Delete
            </Button>
          </Box>
        ) : null}
      </Box>
    );
  }
}

export function ActiveComponentInteriorConditionSettingsProperties() {
  const dispatch = useAppDispatch();

  const interiorConditionSettings = useAppSelector(
    (state) => getActiveFeaturePatternTrussSettings(state)!.interiorConditionSettings,
  );

  return (
    <InteriorConditionSettingsProperties
      values={interiorConditionSettings}
      onChange={(newValues) => dispatch(updateActiveInteriorConditionSettings(newValues))}
    />
  );
}
